import { useState, FormEvent, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";

export default function Login() {
  const { login } = useAuth();
  const { showToast } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [requiresOtp, setRequiresOtp] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
    if (location.search?.includes("activation=required")) {
      setError("Your account is not yet activated. Please contact the admin team to activate your project.");
    }
  }, [location.state, location.search]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userRes = await login(email, password, otp);
      const role = (userRes as any)?.role;
      showToast("success", "Welcome back!");
      navigate(role === "admin" || role === "staff" ? "/admin" : "/dashboard");
    } catch (err: unknown) {
      const errMessage = err instanceof Error ? err.message : "Login failed";
      setError(errMessage);
      // Check if error indicates OTP is required
      if (errMessage.includes("One-time password required")) {
        setRequiresOtp(true);
      }
      // Check if backend response indicates OTP is required
      if ((err as any)?.response?.data?.requiresOtp) {
        setRequiresOtp(true);
        setError("One-time password required for first-time login");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-black flex font-inter">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20futuristic%20technology%20abstract%20background%20glowing%20electric%20blue%20circuit%20patterns%20deep%20space%20digital%20art%20cinematic&width=900&height=1080&seq=auth-bg-001&orientation=portrait"
          alt="Auth background"
          className="w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-deep-black/20 to-deep-black/60" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <Link to="/" className="flex items-center gap-3 mb-auto mt-8">
            <img src="https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-white text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>SCHEMACKOZO</span>
          </Link>
          <blockquote className="mb-8">
            <p className="text-white text-2xl font-bold leading-snug mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              "Engineering SaaS.<br />Powering Growth."
            </p>
            <p className="text-silver text-sm">Access your client portal to track projects, manage invoices, and get support.</p>
          </blockquote>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="flex items-center gap-3 mb-8 lg:hidden">
            <img src="https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png" alt="Logo" className="h-7 w-auto" />
            <span className="font-bold text-white text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>SCHEMACKOZO</span>
          </Link>

          <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Welcome back</h1>
          <p className="text-silver text-sm mb-8">Sign in to your account to continue.</p>



          {successMessage && (
            <div className="flex items-center gap-2 bg-cyber-green/10 border border-cyber-green/30 rounded-xl px-4 py-3 mb-5">
              <i className="ri-check-circle-line text-cyber-green text-sm" />
              <span className="text-cyber-green text-sm">{successMessage}</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-cyber-red/10 border border-cyber-red/30 rounded-xl px-4 py-3 mb-5">
              <i className="ri-error-warning-line text-cyber-red text-sm" />
              <span className="text-cyber-red text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-silver text-xs font-medium uppercase tracking-wider">Password</label>
                <Link to="/forgot-password" className="text-electric-blue text-xs hover:underline cursor-pointer">Forgot password?</Link>
              </div>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 pr-11 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-silver-dim hover:text-white cursor-pointer"
                >
                  <i className={`${showPass ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                </button>
              </div>
            </div>

            {requiresOtp && (
              <div>
                <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">One-Time Password</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter OTP from your email"
                  className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                />
                <p className="text-silver-dim text-xs mt-2">Check your email for the one-time password sent during registration.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-electric-blue text-deep-black font-bold py-3.5 rounded-xl hover:shadow-glow-blue transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  Signing in...
                </span>
              ) : "Sign In"}
            </button>
          </form>

          <p className="text-center text-silver-dim text-sm mt-6">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-electric-blue hover:underline cursor-pointer">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
